import { useCallback, useEffect, useMemo, useState } from 'react';
import { FetchApiDataProps } from './types';
import {
  apiOnWidgetFetchData,
  getRelevantAiData,
} from 'src/api/store/apiSlice';
import { useDispatch, useSelector } from 'react-redux';
import isEqual from 'lodash/isEqual';
import { getDataKey } from './utils';

type Props<T> = FetchApiDataProps<T>;
/**
 * Fetch data from the API and return the data and loading state.
 * @param filters - The filters to apply to the API request.
 * @param service - The API service to call.
 * @param forceFetch - If true, the data will be fetched even if it already exists.
 */
const useFetchWidgetData = <T extends unknown>({
  filters,
  forceFetch = false,
  overrideData,
  overrideLoading,
  service,
}: Props<T>) => {
  const isWithOverrideData = useMemo(
    () => overrideData !== undefined,
    [overrideData],
  );
  const isWithOverrideLoading = useMemo(
    () => overrideLoading !== undefined,
    [overrideLoading],
  );
  // WORKAROUND: simulate real widget loading timings
  const staggeredRenderCount = useMemo(() => Math.random() * 1001, []);
  const dispatch = useDispatch();
  const widgetKey = getDataKey(location.pathname, service.name, filters);

  const stateData = useSelector(
    (state) => getRelevantAiData(state, widgetKey),
    isEqual,
  );
  const data = useMemo(() => {
    if (isWithOverrideData) {
      return overrideData;
    }
    return stateData;
  }, [stateData, overrideData, isWithOverrideData]);

  const [loading, setLoading] = useState(!Boolean(data));

  const getData = useCallback(
    () =>
      dispatch(
        apiOnWidgetFetchData<T>()({ service, filters, dataKey: widgetKey }),
      ),
    [dispatch, service, filters, widgetKey],
  );

  const onGetData = useCallback(() => {
    if (data && !forceFetch) {
      setLoading(false);
      return;
    }

    setLoading(true);
    getData();
  }, [filters, service, forceFetch, data, getData, location]);

  useEffect(() => {
    if (isWithOverrideData) return;

    if (data && loading) {
      setLoading(false);
    }
  }, [data, loading, isWithOverrideData]);

  useEffect(() => {
    if (isWithOverrideData) return;

    const debounceTimeout = setTimeout(() => {
      onGetData();
    }, 1000 + staggeredRenderCount);

    return () => clearTimeout(debounceTimeout);
  }, [staggeredRenderCount, isWithOverrideData, onGetData]);

  return {
    data: (data ?? null) as T | null,
    loading: isWithOverrideLoading ? overrideLoading ?? false : loading,
  };
};

export default useFetchWidgetData;
